import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { adminApi, type Customer } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, cn } from '@/lib/utils';
import { Search, Download, Eye, Users, ChevronRight, RefreshCw } from 'lucide-react';

export function Customers() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['customers', { search, status: statusFilter }],
    queryFn: () => adminApi.getCustomers({ search: search || undefined, status: statusFilter, limit: 50 }),
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Customers</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage your customer base</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => refetch()} 
            className="p-3 rounded-xl bg-secondary/50 active:scale-95 active:bg-secondary transition-all"
          >
            <RefreshCw className={cn("h-5 w-5 text-muted-foreground", isLoading && "animate-spin")} />
          </button>
          <button 
            onClick={() => adminApi.exportCustomers()} 
            className="p-3 rounded-xl bg-secondary/50 active:scale-95 active:bg-secondary transition-all hidden sm:block"
          >
            <Download className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search customers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-12 h-12 rounded-xl text-base"
        />
      </div>

      {/* Filter Pills */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
        {[
          { value: undefined, label: 'All' },
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
          { value: 'cancelled', label: 'Cancelled' },
          { value: 'past_due', label: 'Past Due' },
        ].map((filter) => (
          <button
            key={filter.label}
            onClick={() => setStatusFilter(filter.value)}
            className={cn(
              "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all active:scale-95",
              statusFilter === filter.value
                ? "bg-foreground text-background"
                : "bg-secondary text-muted-foreground"
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center h-[50vh]">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-muted border-t-foreground" />
        </div>
      ) : data?.customers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Users className="h-14 w-14 text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">No customers found</p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden lg:block rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="text-xs uppercase tracking-wider">Customer</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Plan</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Instance</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Created</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.customers.map((customer: Customer) => (
                  <TableRow key={customer.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{customer.email}</p>
                        <p className="text-xs text-muted-foreground">{customer.name || 'No name'}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={customer.plan === 'pro' ? 'default' : 'secondary'}>
                        {customer.plan}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <SubscriptionBadge status={customer.subscriptionStatus} />
                    </TableCell>
                    <TableCell>
                      {customer.instance ? (
                        <div className="flex items-center gap-2">
                          <div className={`status-dot status-dot-${customer.instance.status}`} />
                          <span className="text-sm">{customer.instance.status}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">None</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(customer.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link to="/customers/$customerId" params={{ customerId: customer.id }}>
                        <Button variant="ghost" size="icon-sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-3">
            {data?.customers.map((customer: Customer) => (
              <CustomerCard key={customer.id} customer={customer} />
            ))}
          </div>
        </>
      )}

      {data && (
        <p className="text-xs text-muted-foreground text-center">
          {data.customers.length} of {data.total} customers
        </p>
      )}
    </div>
  );
}

function SubscriptionBadge({ status }: { status: string }) {
  const variant = {
    active: 'success',
    past_due: 'warning',
    cancelled: 'destructive',
  }[status] || 'secondary';

  return (
    <Badge variant={variant as any}>
      {status.replace('_', ' ')}
    </Badge>
  );
}

function CustomerCard({ customer }: { customer: Customer }) {
  return (
    <Link to="/customers/$customerId" params={{ customerId: customer.id }}>
      <div className="mobile-card flex items-center gap-3">
        {/* Avatar placeholder */}
        <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center shrink-0">
          <span className="text-lg font-medium text-muted-foreground">
            {customer.email.charAt(0).toUpperCase()}
          </span>
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{customer.email}</p>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant={customer.plan === 'pro' ? 'default' : 'secondary'} className="text-xs">
              {customer.plan}
            </Badge>
            <SubscriptionBadge status={customer.subscriptionStatus} />
            {customer.instance && (
              <div className={`status-dot status-dot-${customer.instance.status}`} />
            )}
          </div>
        </div>
        
        <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
      </div>
    </Link>
  );
}
